import { useState } from 'react';
import { Box, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TextBlock } from '../../../Atoms/Text';
import { MatrixCategory, MatrixFeature, MatrixFeatureHome } from '../../../../types/advise';
import { categoryCount, selectionCountLabel } from './FeatureMatrixPanel.calc';
import { InlineAddField } from './InlineAddField';
import { MATRIX_ADD_BUTTON } from './matrixAddButton';
import { MATRIX_NAME_LIMIT } from './matrixLimits';

export interface MatrixCategoryBlockProps {
  category: MatrixCategory;
  canEdit: boolean;
  onSelectFeature: (featureId: string, selected: boolean) => void;
  onAddFeature?: (home: MatrixFeatureHome, label: string) => Promise<string | null>;
  onRemoveFeature?: (featureId: string) => Promise<string | null>;
}

// A feature Discovery added goes only with its option there, which would otherwise add it back.
const removable = (feature: MatrixFeature) => feature.custom && !feature.from_discovery;

export const MatrixCategoryBlock = ({
  category,
  canEdit,
  onSelectFeature,
  onAddFeature,
  onRemoveFeature
}: MatrixCategoryBlockProps) => {
  // Kept per feature, so one removal's answer never overwrites another's still on its way.
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [refusals, setRefusals] = useState<Record<string, string>>({});

  const remove = async (featureId: string) => {
    if (!onRemoveFeature) return;
    setRemoving((ids) => new Set(ids).add(featureId));
    const refused = await onRemoveFeature(featureId);
    setRemoving((ids) => {
      const next = new Set(ids);
      next.delete(featureId);
      return next;
    });
    setRefusals(({ [featureId]: _previous, ...others }) => (refused ? { ...others, [featureId]: refused } : others));
  };

  return (
    <Box
      className="mt-3"
      data-testid={`matrix-category-${category.key}`}>
      <TextBlock
        size="xs"
        weight="semibold"
        color="grey6">
        {`${category.label.toUpperCase()} · ${selectionCountLabel(categoryCount(category))}`}
      </TextBlock>
      <Box className="grid grid-cols-1 md:grid-cols-3 gap-x-4 items-center">
        {category.features.map((feature) => (
          <Box
            key={feature.id}
            className="flex items-center">
            <FormControlLabel
              label={feature.label}
              disabled={!canEdit}
              control={
                <Checkbox
                  size="small"
                  checked={feature.selected}
                  onChange={(event) => onSelectFeature(feature.id, event.target.checked)}
                />
              }
              slotProps={{ typography: { fontSize: 13 } }}
            />
            {canEdit && onRemoveFeature && removable(feature) && (
              <IconButton
                size="small"
                aria-label={`Remove ${feature.label}`}
                disabled={removing.has(feature.id)}
                onClick={() => remove(feature.id)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
      {Object.entries(refusals).map(([featureId, refused]) => (
        <TextBlock
          key={featureId}
          size="xs"
          color="red">
          {`${category.features.find((feature) => feature.id === featureId)?.label ?? 'Feature'}: ${refused}`}
        </TextBlock>
      ))}
      {canEdit && onAddFeature && (
        <InlineAddField
          label="+ Add custom feature"
          maxLength={MATRIX_NAME_LIMIT}
          onAdd={(label) => onAddFeature({ categoryId: category.id }, label)}
          buttonProps={MATRIX_ADD_BUTTON}
        />
      )}
    </Box>
  );
};
